import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Flex,
  Heading,
  Icon,
  Stack,
  Tag,
  Text,
} from "@chakra-ui/react";
import { FiChevronRight } from "react-icons/fi";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  FrameworkAgreementDto,
  FrameworkAgreementPatchDto,
  FrameworkAgreementStatus,
  bffApi,
  useGetFrameworkAgreementQuery,
  useUpdateFrameworkAgreementMutation,
} from "../../autogen/bff-api";
import { EntityViewSkeleton } from "../../common/EntityViewSkeleton";
import { createPanelEdits } from "./panelEdits";
import {
  useApiErrorEditorConfig,
  useCreateEditor,
  useEntityState,
} from "../../common/editing/save-coordinator";
import { EntityUnavailable } from "../../common/errors/EntityUnavailable";
import { useNavLabel } from "../../common/layout/useNavLabel";
import { useAppDispatch } from "../../common/redux/hooks";
import { urls } from "../../urls";
import { saveWithVersionConflictRetry } from "../sourcing-events/buyer/wizard/saveWithVersionConflictRetry";
import { useTermRefusal } from "./callOffAvailability";
import { CallOffRulesCard } from "./CallOffRulesCard";
import { CallOffsSection } from "./CallOffsSection";
import { CalledOffSoFar, FrameworkAgreementFacts } from "./FrameworkAgreementSummary";
import { FrameworkValueRemindersSection, type ReminderFailureHandler } from "./FrameworkValueRemindersSection";
import { MiniCompetitionsSection } from "./MiniCompetitionsSection";
import { PanelSection } from "./PanelSection";
import { TerminateFrameworkAgreementButton } from "./TerminateFrameworkAgreementButton";
import { useRecordTitle } from "../../common/page-title/PageTitle";

export const FrameworkAgreementPage = () => {
  const { frameworkAgreementId } = useParams();
  const { t } = useTranslation();
  const { data, error, isLoading, isFetching, refetch } = useGetFrameworkAgreementQuery(
    { frameworkAgreementId: frameworkAgreementId ?? "" },
    { skip: !frameworkAgreementId },
  );
  useRecordTitle(data?.title);

  if (error) {
    return (
      <EntityUnavailable
        error={error}
        notFoundMessage={t("This framework agreement doesn't exist, or your organization doesn't have access to it.")}
        forbiddenMessage={t(
          "You don't have access to this framework agreement. Ask an administrator in your organization for access.",
        )}
        loadFailedMessage={t("This framework agreement could not be loaded right now.")}
        onRetry={refetch}
        isRetrying={isFetching}
        back={{ to: urls.app.frameworkAgreements.viewAll, label: t("Back to framework agreements") }}
      />
    );
  }
  if (isLoading || !data || !frameworkAgreementId) return <EntityViewSkeleton />;

  return <FrameworkAgreementEditor framework={data} title={t("Framework agreement")} />;
};

const FrameworkAgreementEditor = ({ framework, title }: { framework: FrameworkAgreementDto; title: string }) => {
  const { t } = useTranslation();
  const navLabel = useNavLabel();
  const dispatch = useAppDispatch();
  const [update] = useUpdateFrameworkAgreementMutation();
  const editorErrorConfig = useApiErrorEditorConfig();
  const frameworkAgreementId = framework.id;
  const panelEdits = useMemo(() => createPanelEdits(), []);
  const lastSavedFields = useRef<Partial<FrameworkAgreementPatchDto>>({});
  const reminderFailure: ReminderFailureHandler = useRef(null);

  const config = useMemo(
    () => ({
      initialState: framework,
      save: async (fields: Partial<FrameworkAgreementPatchDto>) => {
        lastSavedFields.current = fields;
        const panel = fields.panel ? panelEdits.pending() : [];
        const sent = fields.panel ? { ...fields, panel } : fields;
        const saved = await saveWithVersionConflictRetry({
          fields: sent,
          save: async (retryFields) =>
            await update({
              frameworkAgreementId,
              frameworkAgreementPatchDto: retryFields as FrameworkAgreementPatchDto,
            }).unwrap(),
          reloadLatest: async () => {
            await dispatch(
              bffApi.endpoints.getFrameworkAgreement.initiate({ frameworkAgreementId }, { forceRefetch: true }),
            ).unwrap();
          },
        });
        panelEdits.clear(panel);
        return saved;
      },
      ...editorErrorConfig,
      onError: (error: unknown) => {
        const onlyReminders = Object.keys(lastSavedFields.current).every((field) => field === "valueReminders");
        if (onlyReminders && reminderFailure.current) reminderFailure.current(error);
        else editorErrorConfig.onError(error);
      },
    }),
    [dispatch, framework, frameworkAgreementId, update, editorErrorConfig, panelEdits],
  );

  useCreateEditor<FrameworkAgreementDto, FrameworkAgreementPatchDto>(frameworkAgreementId, config);

  const current = useEntityState<FrameworkAgreementDto>(frameworkAgreementId) ?? framework;
  const isActive = current.status === FrameworkAgreementStatus.Active;
  const canEdit = isActive && (current.mayManage ?? false);
  const termRefusal = useTermRefusal(current);

  return (
    <Stack spacing="6">
      <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p={{ base: "4", md: "6" }}>
        <Breadcrumb
          separator={<Icon as={FiChevronRight} color="smMuted" boxSize="3" />}
          fontSize="sm"
          mb="4"
        >
          <BreadcrumbItem>
            <BreadcrumbLink as={RouterLink} to={urls.app.frameworkAgreements.viewAll} color="smPrimary">
              {navLabel("Framework agreements")}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink color="smMuted" isTruncated maxW={{ base: "220px", md: "360px", lg: "520px" }}>
              {current.title}
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        <Box>
          <Flex align="flex-start" justify="space-between" gap="4" flexWrap="wrap">
            <Stack spacing="3" minW="0">
              <Heading size="lg">{current.title}</Heading>
              <Flex align="center" gap="2" flexWrap="wrap">
                <Tag colorScheme={isActive ? "teal" : "gray"}>{t(current.status)}</Tag>
                <Tag colorScheme="gray" variant="subtle">
                  {title}
                </Tag>
              </Flex>
            </Stack>
            {canEdit && <TerminateFrameworkAgreementButton framework={current} />}
          </Flex>
          <Box mt="6" pt="5" borderTop="1px solid" borderColor="smBorderSubtle">
            <FrameworkAgreementFacts framework={current} canEdit={canEdit} />
            {termRefusal && (
              <Text mt="4" fontSize="sm" color="smMuted">
                {termRefusal}
              </Text>
            )}
          </Box>
        </Box>
      </Box>

      <CalledOffSoFar framework={current} />
      <PanelSection frameworkAgreementId={frameworkAgreementId} canEdit={canEdit} panelEdits={panelEdits} />
      <MiniCompetitionsSection framework={current} canEdit={canEdit} />
      <CallOffsSection framework={current} canEdit={canEdit} />
      <CallOffRulesCard frameworkAgreementId={frameworkAgreementId} canEdit={canEdit} />
      <FrameworkValueRemindersSection
        frameworkAgreementId={frameworkAgreementId}
        canEdit={canEdit}
        failureHandler={reminderFailure}
      />
    </Stack>
  );
};
